import { isNumber } from 'lodash';
import { SliderInput } from '@meitu/saas-ui';
import { hexToHsva, hsvaToHexa } from '@meitu/saas-colors/dist/esm/utils/color-convert';
import { type TextLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import { type IFontBackground, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { ColorGroup } from '@meitu/xdesign-widgets';
import { EffectPropsContainer } from '@/components/effect-props-container';
import { EffectPropsGroup } from '@/components/effect-props-group';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useLocaleStore } from '@/i18n';
import { useLayerProps } from '../../hooks';
import { type WidgetCommonProps } from '../../types';
import { Tracker } from '../../utils';

type Props = WidgetCommonProps;

const FALLBACK_COLOR = '#000000ff';

export const BackgroundInspector: React.FC<Props> = ({ layerId, recordId }) => {
    const { $t } = useLocaleStore();

    const editor = useEditor();
    const background = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const textBackground = layerData.textBackground || [];

        return textBackground.length > 0 ? textBackground[0] : null;
    }) as IFontBackground | null;

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const maxRoundWeight = (() => {
        const layer = editor.services.frame.getActivePickLayer();
        return layer ? Math.ceil(Math.min(layer.width, layer.height) / 2) : 100;
    })();

    const hidden = background ? Boolean(background.hidden) : true;
    const color = background ? background.color : FALLBACK_COLOR;
    const hsva = hexToHsva(color);
    const opacity = Math.round(hsva.a * 100);
    const roundWeight = background && background.roundWeight ? Math.round(background.roundWeight) : 0;

    const add = () => {
        if (background) return;
        editor.getInstance<TextLayer>('TextLayer').addBackground(layerId);

        Tracker.addBackground({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            panelPosition: 'secondary',
        });
    };

    const remove = () => {
        if (!background) return;
        editor.getInstance<TextLayer>('TextLayer').removeEffect(layerId, 'textBackground');

        Tracker.removeBackground({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            panelPosition: 'secondary',
        });
    };

    const setVisibility = (flag: boolean) => {
        if (!background) return;
        editor.getInstance<TextLayer>('TextLayer').setEffectVisibility(layerId, 'textBackground', flag);

        Tracker.toggleBackground({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            panelPosition: 'secondary',
        });
    };

    const handleColorPickerOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openColorPicker({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                kind: 'textBackground',
                panelPosition: 'secondary',
            });
        }
    };

    const adjustColor = (value: string, options = { writeHistory: true }) => {
        if (!background) return;

        const payload = { idx: 0, data: { color: value } };

        editor.getInstance<TextLayer>('TextLayer').adjustBackgroundProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustBackgroundProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'secondary',
            });
        }
    };

    const adjustOpacity = (value: number, options = { writeHistory: true }) => {
        if (!background || !isNumber(value)) return;

        const opacity = Math.round(value);
        const hsva = hexToHsva(color);
        const nextColor = hsvaToHexa({ ...hsva, a: opacity / 100 });
        const payload = { idx: 0, data: { color: nextColor } };

        editor.getInstance<TextLayer>('TextLayer').adjustBackgroundProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustBackgroundProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'secondary',
            });
        }
    };

    const adjustRoundWeight = (value: number, options = { writeHistory: true }) => {
        if (!background || !isNumber(value)) return;

        const roundWeight = Math.round(value);
        const payload = { idx: 0, data: { roundWeight } };

        editor.getInstance<TextLayer>('TextLayer').adjustBackgroundProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustBackgroundProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'secondary',
            });
        }
    };

    return (
        <EffectPropsContainer
            icon="BackgroundBold"
            title={$t((v) => v.textStyleAdd.background)}
            showAdd={background == null}
            visible={!hidden}
            onAdd={add}
            onRemove={remove}
            onToggleVisible={() => setVisibility(!hidden)}
        >
            {background ? (
                <div className="text-background-inspector">
                    <EffectPropsGroup disabled={hidden}>
                        {/* 颜色 */}
                        <LabelAction title={$t((v) => v.textStyleAdd.color)} align>
                            <ColorGroup
                                popupTriggerSize={{ width: 58, height: 26 }}
                                color={color}
                                setColor={(color) => adjustColor(color, { writeHistory: false })}
                                setColorAfter={(color, _, { isBrandColor }) => {
                                    adjustColor(color, { writeHistory: true });

                                    if (isBrandColor) {
                                        window._stat_track?.('hb_layers_func_use', {
                                            func_id: '1_107',
                                        });
                                    }
                                }}
                                onOpenChange={handleColorPickerOpenChange}
                                showIcon={false}
                                popupOverlayClassName="effect-color-picker"
                                placement="left"
                                onToggleBizTab={(bizTab) => {
                                    if (bizTab === 'team') {
                                        window._stat_track?.('hb_layers_func_click', {
                                            func_id: '1_107',
                                        });
                                    }
                                }}
                            />
                        </LabelAction>

                        {/* 透明度 */}
                        <LabelAction title={$t((v) => v.textStyleAdd.opacity)} align>
                            <SliderInput
                                showStepBtn
                                value={opacity}
                                min={0}
                                max={100}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustOpacity(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustOpacity(value, { writeHistory: true })}
                            />
                        </LabelAction>

                        {/* 圆角弧度 */}
                        <LabelAction title={$t((v) => v.textLayer.roundRadius)} align>
                            <SliderInput
                                showStepBtn
                                value={roundWeight}
                                min={0}
                                max={maxRoundWeight}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustRoundWeight(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustRoundWeight(value, { writeHistory: true })}
                            />
                        </LabelAction>
                    </EffectPropsGroup>
                </div>
            ) : null}
        </EffectPropsContainer>
    );
};
